import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { DEFAULT_CURRENCY, getCurrencySymbol, isZeroDecimalCurrency } from "@/lib/currencies";
import { resolveStorageUrl } from "@/lib/storage/public-url";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatPrice(price: number | string, currencyCode: string = DEFAULT_CURRENCY): string {
  const numPrice = typeof price === "string" ? Number.parseFloat(price) : price;
  const resolvedCurrency = currencyCode || DEFAULT_CURRENCY;
  if (Number.isNaN(numPrice)) return `${getCurrencySymbol(resolvedCurrency)}0`;

  const isZeroDecimal = isZeroDecimalCurrency(resolvedCurrency);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: resolvedCurrency,
    minimumFractionDigits: 0,
    maximumFractionDigits: isZeroDecimal ? 0 : 2,
  }).format(numPrice);
}

export function formatPriceSimple(price: number | string, currencyCode: string = DEFAULT_CURRENCY): string {
  const numPrice = typeof price === "string" ? Number.parseFloat(price) : price;
  const resolvedCurrency = currencyCode || DEFAULT_CURRENCY;
  if (Number.isNaN(numPrice)) return `${getCurrencySymbol(resolvedCurrency)}0`;

  const symbol = getCurrencySymbol(resolvedCurrency);
  const isZeroDecimal = isZeroDecimalCurrency(resolvedCurrency);

  if (isZeroDecimal) return `${symbol}${Math.round(numPrice)}`;
  return `${symbol}${numPrice.toFixed(2).replace(/\.00$/, "")}`;
}

/**
 * First entry of a `listings.image_urls` value (array or JSON string).
 *
 * Resolves bucket-relative storage paths: two of the four call sites render the
 * result WITHOUT a thumb funnel (`Chat.tsx` puts it in `selectedPhoto`, which
 * the fullscreen `<motion.img src>` reads; `bookmark-login-modal` forwards it as
 * an `image` prop), so the resolve belongs here rather than at each sink. The
 * other two already wrap it in `toSupabasePublicImageThumbUrl` /
 * `useHeicImage`, and the resolver is idempotent.
 *
 * @see lib/storage/url.ts
 * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §3 L3
 */
export function getFirstImageUrl(imageUrls: string | string[] | null | undefined): string | null {
  const first = firstRawImageUrl(imageUrls);
  return first ? resolveStorageUrl(first) : null;
}

function firstRawImageUrl(imageUrls: string | string[] | null | undefined): string | null {
  if (!imageUrls) return null;
  if (Array.isArray(imageUrls)) {
    return imageUrls[0] || null;
  }
  if (typeof imageUrls === "string") {
    try {
      const parsed = JSON.parse(imageUrls || "[]");
      return Array.isArray(parsed) ? (parsed[0] || null) : null;
    } catch {
      return imageUrls || null;
    }
  }
  return null;
}
